import {
  AlertBanner,
  AuthLayout,
  Button,
  describeInviteFailure,
  InviteAcceptForm,
  InviteErrorFallback,
  LoadingPage,
  Typography,
  useInviteAccept,
  type InviteAcceptFormData,
} from '@idu-nightlife/ui-kit';
import { Stack } from '@mui/material';
import { Suspense, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useNavigate } from 'react-router';
import IDULogo from '../../../assets/idu-logo-light.svg?react';

// Manager invite-accept page. The frozen accept-invite paths and the account-creation form come
// from ui-kit; this page owns manager's chrome and copy.

function InvitationSignupForm() {
  const navigate = useNavigate();

  const {
    venueName,
    accountExists,
    acceptAsNewUser,
    acceptAsExistingUser,
    isAcceptingAsExistingUser,
  } = useInviteAccept();

  const [acceptError, setAcceptError] = useState<unknown>(null);

  const onAcceptInvite = () => {
    setAcceptError(null);
    acceptAsExistingUser().then(() => navigate('/login'), setAcceptError);
  };

  const onSubmit = (formData: InviteAcceptFormData) =>
    acceptAsNewUser(formData).then(() => navigate('/'));

  return (
    <AuthLayout
      main={
        <Stack direction="column" spacing={2} sx={{ alignItems: 'center' }}>
          <IDULogo style={{ width: 150, display: 'block' }} height={100} />

          <Typography.Body sx={{ textAlign: 'center' }}>
            You&apos;ve been invited to {venueName ?? 'a venue'}.
          </Typography.Body>

          {accountExists ? (
            <>
              {acceptError !== null && (
                <AlertBanner severity="error">
                  {describeInviteFailure(
                    acceptError,
                    'Could not accept the invitation. Try again.',
                  )}
                </AlertBanner>
              )}
              <Button size="large" onClick={onAcceptInvite} loading={isAcceptingAsExistingUser}>
                Accept
              </Button>
            </>
          ) : (
            <>
              <Typography.Body sx={{ textAlign: 'center' }}>
                Enter your details to create an account and get started.
              </Typography.Body>

              <InviteAcceptForm onSubmit={onSubmit} />
            </>
          )}
        </Stack>
      }
    />
  );
}

const InviteErrorScreen = (props: FallbackProps) => (
  <InviteErrorFallback {...props} logo={IDULogo} />
);

export default function InvitationSignupPage() {
  return (
    <ErrorBoundary FallbackComponent={InviteErrorScreen}>
      <Suspense fallback={<LoadingPage />}>
        <InvitationSignupForm />
      </Suspense>
    </ErrorBoundary>
  );
}
